/* eslint-disable react/no-danger */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericNameField from 'components/form/GenericNameField'
import PhoneNumberField from 'components/form/PhoneNumberField'
import DateField from 'components/form/DateField'
import AccountPinField from 'components/form/AccountPinField'
import EmailField from 'components/form/EmailField'
import CollapsiblePanel from 'components/CollapsiblePanel'
import BrandedButton from 'components/BrandedButton'
import BackButton from 'components/BackButton'
import withInputAnalytics from 'hoc/WithInputAnalytics'
import { validatePersonalInfoForm } from 'utilities/formValidator.util'
import { transitionToStage } from 'containers/Checkout/actions'
import { STAGE_MODEM_SELECT } from 'containers/Checkout/constants'
import {
  loadDetails,
  setFieldValues,
  submitPersonalInfo,
  setFieldErrors,
  clearFieldError,
} from './actions'
import {
  FIRST_NAME_KEY,
  MIDDLE_NAME_KEY,
  LAST_NAME_KEY,
  PHONE_KEY,
  DATE_BIRTH_KEY,
  ACCOUNT_PIN_KEY,
  EMAIL_KEY,
  GA_CATEGORY,
} from './constants'
import styles from './styles.scss'

const TrackedGenericNameField = withInputAnalytics(GA_CATEGORY)(GenericNameField)
const TrackedDateField = withInputAnalytics(GA_CATEGORY)(DateField)
const TrackedPhoneNumberField = withInputAnalytics(GA_CATEGORY)(PhoneNumberField)
const TrackedAccountPinField = withInputAnalytics(GA_CATEGORY)(AccountPinField)
const TrackedEmailField = withInputAnalytics(GA_CATEGORY)(EmailField)

export class PersonalInfoStageComponent extends Component {
  static propTypes = {
    // From redux
    isLoading: PropTypes.bool,
    fieldError: PropTypes.object,
    fieldValues: PropTypes.object,
    askEmail: PropTypes.bool,

    // Redux actions
    loadDetails: PropTypes.func,
    setFieldErrors: PropTypes.func,
    clearFieldError: PropTypes.func,
    setFieldValues: PropTypes.func,
    submitPersonalInfo: PropTypes.func,
    transitionToStage: PropTypes.func,

    // From Wagtail
    title: PropTypes.string,
    text: PropTypes.string,
    buttonText: PropTypes.string,
    accountPinTitle: PropTypes.string,
    accountPinText: PropTypes.string,
    accountPinInfoTitle: PropTypes.string,
    accountPinInfoText: PropTypes.string,
    qffImage: PropTypes.string,
    qffInfo: PropTypes.string,
  }

  static defaultProps = {
    isLoading: false,
    fieldError: {},
    fieldValues: {},
    askEmail: false,
    loadDetails: f => f,
    setFieldErrors: f => f,
    clearFieldError: f => f,
    setFieldValues: f => f,
    submitPersonalInfo: f => f,
    title: 'Personal Information',
    text: '',
    buttonText: 'Continue',
    accountPinTitle: '4 Digit Account PIN',
    accountPinText: '',
    accountPinInfoTitle: '',
    accountPinInfoText: '',
    qffImage: '',
    qffInfo: '',
    transitionToStage: f => f,
  }

  constructor(props) {
    super(props)
    this.state = {
      fieldValues: {
        [FIRST_NAME_KEY]: '',
        [MIDDLE_NAME_KEY]: '',
        [LAST_NAME_KEY]: '',
        [PHONE_KEY]: '',
        [DATE_BIRTH_KEY]: '',
        [ACCOUNT_PIN_KEY]: '',
        [EMAIL_KEY]: '',
        // Allow values from reducer / server to be merged in if present
        ...props.fieldValues,
      },
    }
  }

  componentDidMount() {
    if (!this.props.fieldValues[ACCOUNT_PIN_KEY]) {
      this.props.loadDetails()
    }
  }

  componentWillReceiveProps(nextProps) {
    // Eg. when the initial state is loaded from the server
    this.setState({
      fieldValues: {
        ...this.state.fieldValues,
        ...nextProps.fieldValues,
      },
    })
  }

  componentWillUnmount() {
    // When moving to next step, scroll position is below fold
    window.scrollTo(0, 0)
  }

  /* Field Handlers */
  onFieldChange = (value, fieldname) => {
    const fieldValues = {
      ...this.state.fieldValues,
      [fieldname]: value,
    }
    this.setState({
      fieldValues,
    })
  }

  onFieldFocus = (event) => {
    if (this.props.fieldError[event.target.name]) {
      this.props.clearFieldError(event.target.name)
    }
  }

  onFieldChangeHandler = event =>
    this.onFieldChange(event.target.value, event.target.name)

  onFieldBlur = (event) => {
    if (this.props.fieldValues[event.target.name] !== this.state.fieldValues[event.target.name]) {
      this.props.setFieldValues(this.state.fieldValues)
    }
  }

  /* Handle Submission */
  onPersonalInfoSumit = (event) => {
    // We're setting the props equivalent to the state, so state is safe to use as source of truth
    event.preventDefault()
    const valid = validatePersonalInfoForm(this.state.fieldValues)

    if (valid === true) {
      this.props.setFieldErrors({})
      this.props.submitPersonalInfo(this.state.fieldValues)
    } else {
      this.props.setFieldErrors(Object.assign(
        {},
        valid === true ? {} : valid,
      ))
    }
  }

  onBack = (event) => {
    event.preventDefault()
    this.props.transitionToStage(STAGE_MODEM_SELECT)
  }

  render() {
    const {
      fieldError,
      isLoading,
      askEmail,
      title,
      text,
      buttonText,
      accountPinTitle,
      accountPinText,
      accountPinInfoTitle,
      accountPinInfoText,
      qffImage,
      qffInfo,
    } = this.props

    const firstNameElement = (
      <TrackedGenericNameField
        fieldName={FIRST_NAME_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[FIRST_NAME_KEY]}
        labelText="First Name"
        errorMessage={fieldError[FIRST_NAME_KEY]}
        loading={isLoading}
        isRequired
      />
    )
    const middleNameElement = (
      <TrackedGenericNameField
        fieldName={MIDDLE_NAME_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[MIDDLE_NAME_KEY]}
        labelText="Middle Name"
        errorMessage={fieldError[MIDDLE_NAME_KEY]}
        loading={isLoading}
      />
    )
    const lastNameElement = (
      <TrackedGenericNameField
        fieldName={LAST_NAME_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[LAST_NAME_KEY]}
        labelText="Last Name"
        errorMessage={fieldError[LAST_NAME_KEY]}
        loading={isLoading}
        isRequired
      />
    )
    const phoneNumberElement = (
      <TrackedPhoneNumberField
        fieldName={PHONE_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[PHONE_KEY]}
        errorMessage={fieldError[PHONE_KEY]}
        labelText="Contact Number"
        loading={isLoading}
        isRequired
      />
    )

    // For people coming from NBN's website where they skip email entry eariler in the flow.
    const emailElement = askEmail && (
      <TrackedEmailField
        fieldName={EMAIL_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[EMAIL_KEY]}
        errorMessage={fieldError[EMAIL_KEY]}
        labelText="Email"
        loading={isLoading}
        isRequired
      />
    )

    const dateOfBirthElement = (
      <TrackedDateField
        fieldName={DATE_BIRTH_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[DATE_BIRTH_KEY]}
        errorMessage={fieldError[DATE_BIRTH_KEY]}
        labelText="Date of Birth (DD/MM/YYYY)"
        loading={isLoading}
        isRequired
      />
    )

    const accountPinElement = (
      <TrackedAccountPinField
        fieldName={ACCOUNT_PIN_KEY}
        onChange={this.onFieldChangeHandler}
        onBlur={this.onFieldBlur}
        onFocus={this.onFieldFocus}
        value={this.state.fieldValues[ACCOUNT_PIN_KEY]}
        labelText="PIN"
        errorMessage={fieldError[ACCOUNT_PIN_KEY]}
        loading={isLoading}
        isRequired
      />
    )

    return (
      <div className={styles.container}>
        <h2>{title}</h2>
        <div dangerouslySetInnerHTML={{ __html: text }} />
        <form
          className={styles.formContainer}
          onSubmit={this.onPersonalInfoSumit}
          method="POST"
        >
          <fieldset>
            <legend>Enter your personal information</legend>
            <div className={styles.nameFieldWrapper}>
              <div className={styles.col}>
                {firstNameElement}
              </div>
              <div className={styles.col}>
                {middleNameElement}
              </div>
              <div className={styles.col}>
                {lastNameElement}
              </div>
            </div>
            {emailElement}
            {phoneNumberElement}
            {dateOfBirthElement}
          </fieldset>
          <fieldset>
            <legend>Choose an account PIN</legend>
            <h2>{accountPinTitle}</h2>
            <div dangerouslySetInnerHTML={{ __html: accountPinText }} />
            <div className={styles.accountPinFieldWrapper}>
              {accountPinElement}
            </div>
            { accountPinInfoTitle &&
              <CollapsiblePanel title={accountPinInfoTitle}>
                <div dangerouslySetInnerHTML={{ __html: accountPinInfoText }} />
              </CollapsiblePanel>
            }
          </fieldset>

          <div className={styles.btnWrap}>
            <BrandedButton
              labelText={buttonText}
              className={styles.submitBtn}
            />
            <BackButton onClick={this.onBack} />
          </div>
          { qffImage &&
            <div className={styles.qffInfoWrapper}>
              <img src={qffImage} alt="qff" />
              <div dangerouslySetInnerHTML={{ __html: qffInfo }} />
            </div>
          }
        </form>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    fieldValues: state.checkoutReducer.personalFieldValues,
    fieldError: state.checkoutReducer.personalFieldError,
    isLoading: state.checkoutReducer.isLoading,
    askEmail: !state.checkoutReducer.email,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      loadDetails,
      setFieldErrors,
      clearFieldError,
      setFieldValues,
      submitPersonalInfo,
      transitionToStage,
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(PersonalInfoStageComponent)



// WEBPACK FOOTER //
// ./containers/stages/PersonalInfoStage/index.jsx